/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { alias, or } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { timeout } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import b64EncodeUnicode from 'cio/utils/base64-unicode';
import config from 'cio/config/environment';
import actionInfo, { actionTypeName } from 'cio/utils/action-info';

import DS from 'ember-data';
import RouterService from '@ember/routing/router-service';
import { ApiError } from 'cio/services/api';
import AccountService from 'cio/services/account';
import AnalyticsService from 'cio/services/analytics';
import { Channel } from 'cio/utils/metrics/common';
import { ActionType } from 'cio/models/action';
import MessageLibraryItemModel, { MessageLibraryItemStatus } from 'cio/models/message-library-item';
import { ALL_STATUSES_FILTERS, StatusesFilter, SortProperty } from 'cio/pods/message-library/controller';
import { SortDirection } from 'cio/utils/sort';
import { isPresent } from '@ember/utils';
import MessageLibraryIndexStatusFilterItem from './status-filter-list-item';
import HydraService from 'cio/services/hydra';

interface MessageLibraryIndexArgs {
  availableChannelActionTypes: ActionType[];
  channels: Channel[];
  excludeArchived: boolean;
  page: number;
  query: string;
  sortBy: SortProperty;
  sortDirection: SortDirection;
  statuses: StatusesFilter[];
  onChannelsChange: (channels: Channel[]) => unknown;
  onExcludeArchivedChange: (excludeArchived: boolean) => unknown;
  onPageChange: (page: number) => unknown;
  onQueryChange: (query: string) => unknown;
  onSortChange: (sortBy: SortProperty, sortDirection: SortDirection) => unknown;
  onStatusesChange: (statuses: string[]) => unknown;
}

const INVALID_SEARCH_TERM_LENGTH_ERROR = 'invalid search term length';
const DEBOUNCE_DURATION = config.APP.debounceDuration;

const STATUS_FILTER_ITEMS = {
  [StatusesFilter.Active]: {
    label: MessageLibraryItemStatus.Active,
    value: StatusesFilter.Active,
  },
  [StatusesFilter.Automatic]: {
    label: MessageLibraryItemStatus.Automatic,
    value: StatusesFilter.Automatic,
  },
  [StatusesFilter.Draft]: {
    label: MessageLibraryItemStatus.Draft,
    value: StatusesFilter.Draft,
  },
  [StatusesFilter.Drafted]: {
    label: MessageLibraryItemStatus.Drafted,
    value: StatusesFilter.Drafted,
  },
  [StatusesFilter.Off]: {
    label: MessageLibraryItemStatus.Off,
    value: StatusesFilter.Off,
  },
  [StatusesFilter.Sent]: {
    label: MessageLibraryItemStatus.Sent,
    value: StatusesFilter.Sent,
  },
};

export default class MessageLibraryIndex extends Component<MessageLibraryIndexArgs> {
  @service account!: AccountService;
  @service analytics!: AnalyticsService;
  @service router!: RouterService;
  @service store!: DS.Store;
  @service hydra!: HydraService;

  @tracked hasSearchLengthError = false;

  emptySearchImage = new URL('cio/assets/images/illustrations/empty-search.png', import.meta.url).toString();

  @alias('queryTask.lastSuccessful.value') messages!: DS.RecordArray<MessageLibraryItemModel>;
  @or('queryTask.isRunning', 'setSearchTask.isRunning') isSearching!: boolean;

  channelDropdownGroups = [
    {
      label: 'All channels',
      value: 'all_channels',
      items: this.args.availableChannelActionTypes.map((type: ActionType) => {
        let info = actionInfo(type);

        return {
          label: info.name,
          value: actionTypeName(type),
          icon: info.icon,
        };
      }),
    },
  ];

  statusDropdownGroups = [
    {
      label: 'All statuses',
      value: 'all_statuses',
      items: ALL_STATUSES_FILTERS.map((status: StatusesFilter) => ({
        ...STATUS_FILTER_ITEMS[status],
        listItemComponent: MessageLibraryIndexStatusFilterItem,
      })),
    },
  ];

  get hasFiltersApplied() {
    return (
      this.args.channels.length !== this.args.availableChannelActionTypes.length ||
      this.args.statuses.length !== ALL_STATUSES_FILTERS.length ||
      isPresent(this.args.query) ||
      this.args.excludeArchived
    );
  }

  startSearchTask = task({ restartable: true }, async (value: string) => {
    this.hasSearchLengthError = false;

    await timeout(DEBOUNCE_DURATION);

    this.args.onQueryChange(value);
  });

  queryTask = task({ restartable: true }, async () => {
    try {
      let messages = await this.store.query('message-library-item', {
        asc: this.args.sortDirection === SortDirection.Ascending,
        page: this.args.page,
        search: b64EncodeUnicode(this.args.query),
        sort: this.args.sortBy,
        channel: this.args.channels,
        archived: !this.args.excludeArchived,
        status: this.args.statuses,
      });

      return messages;
    } catch (error: any) {
      let errors = (error.errors as ApiError[] | null) ?? [];
      let [firstError] = errors;

      if (firstError?.detail === INVALID_SEARCH_TERM_LENGTH_ERROR) {
        return (this.hasSearchLengthError = true);
      } else {
        // rethrow error so it can bubble up to the environment, which reports
        // it to Sentry and shows an error route
        throw error;
      }
    }
  });
}
